import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarIcon, Package, Plane, Building2 } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface EditBookingDialogProps {
  booking: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBookingUpdated?: () => void;
}

export const EditBookingDialog = ({ 
  booking, 
  open, 
  onOpenChange, 
  onBookingUpdated 
}: EditBookingDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    clientName: "",
    clientPhone: "",
    clientEmail: "",
    service: "",
    destination: "",
    checkInDate: undefined as Date | undefined,
    checkOutDate: undefined as Date | undefined,
    passengerCount: 1,
    roomCount: 1,
    price: "",
    currency: "SAR",
    status: "confirmed",
    notes: "",
  });

  useEffect(() => {
    if (booking) {
      setFormData({
        clientName: booking.clientName || "",
        clientPhone: booking.clientPhone || "",
        clientEmail: booking.clientEmail || "",
        service: booking.service || "",
        destination: booking.destination || "",
        checkInDate: booking.checkIn ? new Date(booking.checkIn) : undefined,
        checkOutDate: booking.checkOut ? new Date(booking.checkOut) : undefined,
        passengerCount: booking.passengerCount || 1,
        roomCount: booking.roomCount || 1,
        price: booking.totalAmount?.toString() || "",
        currency: booking.currency || "SAR",
        status: booking.status || "confirmed",
        notes: booking.notes || "",
      });
    }
  }, [booking]);

  const getServiceTypeIcon = (type: string) => {
    switch (type) {
      case "package": return <Package className="h-5 w-5" />;
      case "flight": return <Plane className="h-5 w-5" />;
      case "hotel": return <Building2 className="h-5 w-5" />;
      default: return <Package className="h-5 w-5" />;
    }
  };

  const getServiceTypeName = (type: string) => {
    switch (type) {
      case "package": return "باقة سياحية";
      case "flight": return "حجز طيران";
      case "hotel": return "حجز فندق";
      default: return "خدمة";
    }
  };

  const handleUpdate = async () => {
    if (!formData.clientName || !formData.service) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setLoading(true);
    try {
      // Create modification request
      const originalData = {
        client_name: booking.clientName,
        service: booking.service,
        check_in: booking.checkIn,
        check_out: booking.checkOut,
        passenger_count: booking.passengerCount,
        room_count: booking.roomCount,
        total_amount: booking.totalAmount,
        currency: booking.currency,
        status: booking.status,
        notes: booking.notes,
      };

      const newData = {
        client_name: formData.clientName,
        service: formData.service,
        check_in: formData.checkInDate?.toISOString(),
        check_out: formData.checkOutDate?.toISOString(),
        passenger_count: formData.passengerCount,
        room_count: formData.roomCount,
        total_amount: parseFloat(formData.price) || 0,
        currency: formData.currency,
        status: formData.status,
        notes: formData.notes,
      };

      const { error } = await supabase
        .from("booking_modifications")
        .insert({
          booking_id: booking.id,
          booking_type: booking.type,
          modification_type: "update",
          original_data: originalData as any,
          new_data: newData as any,
          modification_reason: "تحديث بيانات الحجز",
          status: "approved", // Auto-approve for now
          processed_at: new Date().toISOString(),
        });

      if (error) throw error;

      // If it's a package booking, update the actual record
      if (booking.type === "package") {
        const { error: updateError } = await supabase
          .from("package_bookings")
          .update({
            // Use available fields from package_bookings table
            booking_date: formData.checkInDate?.toISOString().split('T')[0],
            end_date: formData.checkOutDate?.toISOString().split('T')[0],
            price: parseFloat(formData.price) || 0,
            currency: formData.currency,
            status: formData.status,
            notes: formData.notes,
          })
          .eq("id", booking.id);

        if (updateError) throw updateError;
      }

      toast.success("تم تحديث الحجز بنجاح");
      onOpenChange(false);
      onBookingUpdated?.();
    } catch (error) {
      console.error("Error updating booking:", error);
      toast.error("حدث خطأ أثناء تحديث الحجز");
    } finally {
      setLoading(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl font-bold">
            {getServiceTypeIcon(booking.type)}
            تعديل {getServiceTypeName(booking.type)} - {booking.bookingNumber}
          </DialogTitle>
        </DialogHeader>

        <Card>
          <CardHeader>
            <CardTitle>بيانات الحجز</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="clientName">اسم العميل *</Label>
                <Input
                  id="clientName"
                  value={formData.clientName}
                  onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                  placeholder="أدخل اسم العميل"
                />
              </div>
              <div>
                <Label htmlFor="clientPhone">رقم الهاتف</Label>
                <Input
                  id="clientPhone"
                  value={formData.clientPhone}
                  onChange={(e) => setFormData({...formData, clientPhone: e.target.value})}
                  placeholder="رقم الهاتف"
                />
              </div>
              <div>
                <Label htmlFor="clientEmail">البريد الإلكتروني</Label>
                <Input
                  id="clientEmail"
                  type="email"
                  value={formData.clientEmail}
                  onChange={(e) => setFormData({...formData, clientEmail: e.target.value})}
                  placeholder="البريد الإلكتروني"
                />
              </div>
              <div>
                <Label htmlFor="service">الخدمة *</Label>
                <Input
                  id="service"
                  value={formData.service}
                  onChange={(e) => setFormData({...formData, service: e.target.value})}
                  placeholder="وصف الخدمة"
                />
              </div>
              
              {booking.type === "package" && (
                <div>
                  <Label htmlFor="destination">الوجهة</Label>
                  <Input
                    id="destination"
                    value={formData.destination}
                    onChange={(e) => setFormData({...formData, destination: e.target.value})}
                    placeholder="وجهة السفر"
                  />
                </div>
              )}

              <div>
                <Label>تاريخ البداية</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.checkInDate ? 
                        format(formData.checkInDate, "PPP", { locale: ar }) : 
                        "اختر التاريخ"
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.checkInDate}
                      onSelect={(date) => setFormData({...formData, checkInDate: date})}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label>تاريخ النهاية</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.checkOutDate ? 
                        format(formData.checkOutDate, "PPP", { locale: ar }) : 
                        "اختر التاريخ"
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.checkOutDate}
                      onSelect={(date) => setFormData({...formData, checkOutDate: date})}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="passengerCount">عدد المسافرين</Label>
                <Input
                  id="passengerCount"
                  type="number"
                  min="1"
                  value={formData.passengerCount}
                  onChange={(e) => setFormData({...formData, passengerCount: parseInt(e.target.value) || 1})}
                />
              </div>

              {(booking.type === "package" || booking.type === "hotel") && (
                <div>
                  <Label htmlFor="roomCount">عدد الغرف</Label>
                  <Input
                    id="roomCount"
                    type="number"
                    min="1"
                    value={formData.roomCount}
                    onChange={(e) => setFormData({...formData, roomCount: parseInt(e.target.value) || 1})}
                  />
                </div>
              )}

              <div>
                <Label htmlFor="price">السعر</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  placeholder="0"
                />
              </div>

              <div>
                <Label htmlFor="currency">العملة</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SAR">ريال سعودي</SelectItem>
                    <SelectItem value="USD">دولار أمريكي</SelectItem>
                    <SelectItem value="EUR">يورو</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status">حالة الحجز</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">مؤكد</SelectItem>
                    <SelectItem value="pending">في الانتظار</SelectItem>
                    <SelectItem value="cancelled">ملغي</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">ملاحظات</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="أي ملاحظات إضافية..."
                rows={3}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                onClick={handleUpdate} 
                disabled={loading}
                className="flex-1"
              >
                {loading ? "جاري التحديث..." : "حفظ التغييرات"}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};